/**
 * GameMonetize SDK Integration
 * 
 * Provides a safe wrapper around the GameMonetize.com SDK with stubs
 * for local development and testing.
 * 
 * @see https://github.com/MonetizeGame/GameMonetize.com-SDK
 * 
 * Game ID: 5it2ku5rbtw1gwqdl0woc9wmqo47e4q6
 */

// GameMonetize SDK type definition
declare global {
  interface Window {
    SDK_OPTIONS?: {
      gameId: string;
      onEvent: (event: { name: string }) => void;
    };
    sdk?: {
      showBanner: () => void;
    };
    // Global callbacks for SDK event handling
    gameMonetizePause?: () => void;
    gameMonetizeResume?: () => void;
  }
}

/**
 * GameMonetize SDK Wrapper
 * 
 * Provides safe access to GameMonetize SDK features with fallback stubs.
 * All methods are safe to call even if SDK is not loaded.
 */
export class GameMonetizeSDK {
  private static instance: GameMonetizeSDK;
  private isInitialized = false;
  private isReady = false;
  private isPaused = false;
  
  // Callbacks for game state
  private onPauseCallback: (() => void) | null = null;
  private onResumeCallback: (() => void) | null = null;
  
  // Your unique Game ID from GameMonetize
  private readonly GAME_ID = '5it2ku5rbtw1gwqdl0woc9wmqo47e4q6';
  
  private constructor() {}
  
  /**
   * Get singleton instance
   */
  static getInstance(): GameMonetizeSDK {
    if (!GameMonetizeSDK.instance) {
      GameMonetizeSDK.instance = new GameMonetizeSDK();
    }
    return GameMonetizeSDK.instance;
  }
  
  /**
   * Initialize SDK connection
   * Safe to call multiple times
   */
  async init(): Promise<boolean> {
    if (this.isInitialized) {
      return this.isReady;
    }
    
    this.isInitialized = true;
    
    try {
      // Register global callbacks for SDK events from index.html
      window.gameMonetizePause = () => {
        this.isPaused = true;
        console.log('[GameMonetizeSDK] Game paused for ad (via global)');
        this.onPauseCallback?.();
      };
      
      window.gameMonetizeResume = () => {
        this.isPaused = false;
        console.log('[GameMonetizeSDK] Game resumed after ad (via global)');
        this.onResumeCallback?.();
      };
      
      // Check if already loaded
      if (window.sdk?.showBanner) {
        this.isReady = true;
        console.log('[GameMonetizeSDK] SDK already loaded');
        return true;
      }
      
      // Wait for SDK to be ready (with timeout)
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds timeout
        
        const checkReady = setInterval(() => {
          attempts++;
          
          if (window.sdk?.showBanner) {
            clearInterval(checkReady);
            this.isReady = true;
            console.log('[GameMonetizeSDK] SDK initialized successfully');
            resolve(true);
          } else if (attempts >= maxAttempts) {
            clearInterval(checkReady);
            console.log('[GameMonetizeSDK] SDK not available, using stubs');
            resolve(false);
          }
        }, 100);
      });
    } catch (error) {
      console.warn('[GameMonetizeSDK] Failed to initialize:', error);
      return false;
    }
  }
  
  /**
   * Check if SDK is available
   */
  isAvailable(): boolean {
    return this.isReady && window.sdk?.showBanner !== undefined;
  }
  
  /**
   * Check if game is currently paused for an ad
   */
  isGamePaused(): boolean {
    return this.isPaused;
  }
  
  /**
   * Set callback for when game should pause (ad starting)
   */
  setOnPause(callback: () => void): void {
    this.onPauseCallback = callback;
  }
  
  /**
   * Set callback for when game should resume (ad finished)
   */
  setOnResume(callback: () => void): void {
    this.onResumeCallback = callback;
  }
  
  /**
   * Show an interstitial advertisement
   * Call this at natural break points (level complete, game over, etc.)
   */
  showAd(): void {
    if (!this.isAvailable()) {
      // No SDK, simulate ad
      console.log('[GameMonetizeSDK] showAd - no SDK, simulating');
      this.onPauseCallback?.();
      setTimeout(() => {
        this.onResumeCallback?.();
      }, 500);
      return;
    }
    
    try {
      console.log('[GameMonetizeSDK] Requesting ad...');
      window.sdk!.showBanner();
    } catch (error) {
      console.warn('[GameMonetizeSDK] showBanner failed:', error);
      this.onResumeCallback?.();
    }
  }
  
  /**
   * Request an interstitial ad with promise
   * @returns Promise that resolves when ad is complete
   */
  async requestAd(): Promise<boolean> {
    return new Promise((resolve) => {
      const originalResume = this.onResumeCallback;
      
      // Temporarily override resume callback
      this.onResumeCallback = () => {
        this.onResumeCallback = originalResume;
        originalResume?.();
        resolve(true);
      };
      
      this.showAd();
      
      // Timeout fallback (ads shouldn't take more than 60 seconds)
      setTimeout(() => {
        if (this.isPaused) {
          this.onResumeCallback = originalResume;
          resolve(false);
        }
      }, 60000);
    });
  }
  
  /**
   * Notify SDK that gameplay has started
   * Call when player starts actual gameplay
   */
  gameplayStart(): void {
    console.log('[GameMonetizeSDK] Gameplay started');
    // GameMonetize doesn't have a specific gameplay event
    // but we track it internally
  }
  
  /**
   * Notify SDK that gameplay has stopped
   * Good time to show an ad!
   */
  gameplayStop(): void {
    console.log('[GameMonetizeSDK] Gameplay stopped');
    // This is a good time to show an ad
  }
  
  /**
   * Get the Game ID
   */
  getGameId(): string {
    return this.GAME_ID;
  }
}

export default GameMonetizeSDK;
